import {
  AppBar,
  AppBarIdentity,
  AppBarName,
  Avatar,
  Bubble,
  Chip,
  ChipRow,
  Composer,
  ComposerField,
  ComposerMic,
  ComposerPlus,
  IconButton,
  ImageBubble,
  MessageTime,
  RichCard,
  RichCardCta,
  RichCardText,
  Stamp,
  ThreadContainer,
  ThreadStack,
} from "./android.styles";
import {
  ArrowBackGlyph,
  EmojiGlyph,
  GalleryGlyph,
  MicGlyph,
  MoreVertGlyph,
  PersonGlyph,
  PhoneGlyph,
  PlusGlyph,
} from "./androidGlyphs";
import { ContactCardPreview } from "../ContactCardPreview";
import type { PhoneMessage } from "../phoneMessage";
import { renderMessageWithUrls } from "../previewContentUtils";
import { PreviewErrorBubble } from "../PreviewErrorBubble";

import {
  LinkIcon,
  PhoneIcon,
  ShoppingCartIcon,
  SmsOutlinedIcon,
} from "@/icons";

const SUGGESTION_CHIP_ICONS = {
  REPLY: SmsOutlinedIcon,
  OPEN_URL: LinkIcon,
  DIAL_PHONE: PhoneIcon,
  PRODUCT: ShoppingCartIcon,
} as const;

function MessageBody({ message }: { message: PhoneMessage }) {
  const isRichCard =
    message.channel === "RCS" &&
    (message.imageUrl !== null || message.ctaText !== null);

  return (
    <>
      {isRichCard ? (
        <RichCard data-testid="android-bubble">
          {message.imageUrl ? (
            <img src={message.imageUrl} alt={message.imageAlt} />
          ) : null}
          {message.text ? (
            <RichCardText hint={message.showEmptyHint}>
              {renderMessageWithUrls(message.text, true)}
            </RichCardText>
          ) : null}
          {message.ctaText ? (
            <RichCardCta>{message.ctaText}</RichCardCta>
          ) : null}
        </RichCard>
      ) : (
        <>
          {message.imageUrl ? (
            <ImageBubble>
              <img src={message.imageUrl} alt={message.imageAlt} />
            </ImageBubble>
          ) : null}
          {message.text || !message.imageUrl ? (
            <Bubble data-testid="android-bubble" hint={message.showEmptyHint}>
              {renderMessageWithUrls(message.text, true)}
            </Bubble>
          ) : null}
        </>
      )}

      {message.documentName ? (
        <ContactCardPreview name={message.documentName} />
      ) : null}

      <MessageTime>9:41 AM</MessageTime>

      {message.suggestions.length > 0 ? (
        <ChipRow>
          {message.suggestions.map((suggestion, index) => {
            const Glyph = SUGGESTION_CHIP_ICONS[suggestion.type];
            return (
              <Chip key={index}>
                <Glyph />
                {suggestion.text}
              </Chip>
            );
          })}
        </ChipRow>
      ) : null}
    </>
  );
}

interface AndroidMessagesScreenProps {
  message: PhoneMessage;
  onRetry?: () => void;
}

export function AndroidMessagesScreen({
  message,
  onRetry,
}: AndroidMessagesScreenProps) {
  return (
    <>
      <AppBar>
        <IconButton>
          <ArrowBackGlyph />
        </IconButton>
        <AppBarIdentity>
          <Avatar>
            <PersonGlyph size={24} />
          </Avatar>
          <AppBarName>{message.sender}</AppBarName>
        </AppBarIdentity>
        <IconButton>
          <PhoneGlyph />
        </IconButton>
        <IconButton>
          <MoreVertGlyph />
        </IconButton>
      </AppBar>

      <ThreadContainer>
        <ThreadStack>
          <Stamp>Tue, Sep 8 · 9:41 AM</Stamp>
          {message.isError ? (
            <PreviewErrorBubble
              onRetry={onRetry}
              maxWidth={300}
              borderRadius={20}
              fontSize={16}
              lineHeight={24}
            />
          ) : (
            <MessageBody message={message} />
          )}
        </ThreadStack>
      </ThreadContainer>

      <Composer>
        <ComposerPlus>
          <PlusGlyph />
        </ComposerPlus>
        <ComposerField>
          <EmojiGlyph />
          <span>
            {message.channel === "RCS" ? "RCS message" : "Text message"}
          </span>
          <GalleryGlyph />
        </ComposerField>
        <ComposerMic>
          <MicGlyph />
        </ComposerMic>
      </Composer>
    </>
  );
}
